import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { sendOrder } from '../reducers/orderSlice';
import { TERMINAL_ORDER_STATUS_CODES } from '../reducers/orderSlice.constants';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import {
  selectIsPosmonDisconnected,
  selectLatestOrderRequest,
} from '../selectors/order';
import {
  selectHypothesisCartItems,
  selectValidCartItems,
} from '../selectors/cart';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import {
  selectFeatureFlagOrderCheckRetry,
  selectOrderCheckTimeoutSeconds,
} from '../redux/features/config/config.selector';
import { AgentInterceptionType } from '../reducers/messagingSlice';
import useAgentInterception from './agentInterception/useAgentInterception.hooks';
import logger from '../utils/logger';

/**
 * Detects a stuck order — no check event within ORDER_CHECK_TIMEOUT_SECONDS of
 * the order event being sent — and exposes an agent-triggered retry.
 *
 * POSMON occasionally goes quiet without emitting a `disconnect` event: the
 * order goes out, no check ever comes back, and the cart lines sit at
 * SENDING/PENDING (the progress bar never turns green). Agents work around it
 * today by adding an item they don't need (typically a water) and removing it
 * to force a resend. This hook watches the latest order request — the only one
 * whose check event the orderSlice processes — and reports when its check is
 * overdue so the UI can offer a Retry button instead; `retry()` re-dispatches
 * `sendOrder`, which issues a fresh order event for the same cart, and emits a
 * METRIC event so we can quantify how often agents need it.
 *
 * The overdue clock runs per order event (from the request's orderSentAt) and
 * stops ONLY when the check event arrives. An order_status alone does not stop
 * it — deliberately, so the retry also covers orders the POS acked but never
 * finished, and orders POSLINK rejected with the 7002 "Send Order is Pending"
 * order_status error, which never get a check (reducing that error at its
 * source is HITL-1417). There is no retry cap on CLICKS: each click is an
 * explicit agent action, and a retry sends a new order event whose own clock
 * restarts.
 *
 * Auto-retry (HITL-1515): once the banner is up, a countdown runs (the same
 * ORDER_CHECK_TIMEOUT_SECONDS again — one `order_check_retry` flag and one
 * variant value govern the banner and the auto-send together; anchored to
 * orderSentAt, so a re-render can't restart it) and the resend fires on its
 * own if the agent doesn't click first. Unlike clicks, the auto-resend IS capped: one per stuck
 * episode, and ANY retry — auto or clicked — spends it (`isAutoRetrySpent`).
 * A check event finally landing (or the request set resetting) ends the
 * episode and re-arms it. So if a resend's order also goes unconfirmed, the
 * banner returns as a plain manual Retry: a dead POS is never resent to on a
 * loop, and an agent who just clicked doesn't get an immediate surprise
 * auto-send on top (the countdown deadline for the old request is already in
 * the past, so re-arming it after a click would fire instantly). The spent
 * flag is per-mount state: OrderReview stays mounted for the life of a
 * session, so in practice that is per stuck episode.
 *
 * A resend cannot duplicate the order on the POS side: every order event
 * carries the full cart snapshot, not a delta — the same property today's
 * add-a-water-and-remove-it workaround relies on.
 *
 * A clicked retry disables further clicks (`isRetrying`) until the resend is
 * recorded as a new latest request. sendOrder on the menu-module path only
 * records the request after its HTTP call resolves, so without the guard a
 * double-click sends two order events for the same cart — and since only the
 * latest request's check is processed, the check answering the first click
 * would be discarded, recreating the stuck state. (On the legacy websocket
 * path the request is recorded synchronously, so the guard clears in the same
 * commit — it only has teeth on the menu-module path.) If no new request ever
 * appears (the resend failed before recording one), a fallback re-enables the
 * button after the check timeout.
 *
 * `isCheckOverdue` stays false when:
 * - the `order_check_retry` flag is off, or the timeout is 0;
 * - the cart is empty AND the order is not a final (sendOrder returns early
 *   with nothing valid to send, so a Retry would be a dead click; finals keep
 *   the clock for the banner's unconfirmed display — see the FINAL section);
 * - the POS answered with a TERMINAL order_status error (7008 "Final order
 *   already received" — e.g. an agent editing a cart inherited after the AI
 *   already finalized): no resend can ever succeed, so offering Retry next to
 *   that red error would be a false promise. Retryable errors (7002) and
 *   silence keep the clock running;
 * - POSMON reported a disconnect (the reconnect effect in OrderReview already
 *   resends the cart when the `connect` event arrives).
 *
 * Stuck FINAL orders (HITL-1527, scope settled in the !1588 review): the
 * overdue CLOCK runs for finals even on an empty cart, so the banner can show
 * an honest "Final order unconfirmed" instead of a forever-green "awaiting
 * POS". But RETRY is offered only for AI-SUBMITTED finals (`canRetry`):
 * - AI final, agent supervising: the finish reset waits for the check, so the
 *   cart is still live and a resend carries the real items. Manual only — the
 *   auto-resend countdown never arms for finals: an automatic duplicate final
 *   is the HITL-1513 incident class, so resubmitting must be a deliberate
 *   click. The retry resends AS final; if the POS actually has the original,
 *   the resend answers 7008, which the terminal guard turns into the rejected
 *   state instead of another offer.
 * - HITL-clicked final: NO retry, by decision. resetStatesAfterFinalOrderSubmit
 *   clears the cart in the same chain that sends the final, and every resend
 *   path rebuilds from the live cart — a "retry" here would send an EMPTY
 *   final. The banner tracks the final and reports it unconfirmed; recovery is
 *   deliberately not automated.
 *
 * AI auto mode (HITL-1505): the banner DOES show while the AI is driving, so a
 * monitoring agent sees a stuck AI order without having to take over first.
 * Because sendOrder refuses to send in AI auto mode, a click in that state
 * first dispatches the standard agent interception (ending AI auto mode, the
 * same takeover every other agent action performs) and then resends. The
 * AUTO-resend never fires while the AI is driving — the countdown arms only
 * once the agent has control (see isAutoRetryArmed).
 */
const useOrderCheckRetry = ({
  isUnifiedMenu,
  menuUnificationOrderFailureCallback,
  menuUnificationOrderSuccessCallback,
}: {
  isUnifiedMenu: boolean;
  menuUnificationOrderFailureCallback: Function;
  menuUnificationOrderSuccessCallback: Function;
}) => {
  const dispatch = useAppDispatch();
  const isEnabled = useAppSelector(selectFeatureFlagOrderCheckRetry);
  const timeoutSeconds = useAppSelector(selectOrderCheckTimeoutSeconds);
  // The auto-resend countdown reuses the check timeout: one `order_check_retry`
  // flag and one variant value govern the banner and the auto-send together.
  const autoRetrySeconds = timeoutSeconds;
  const latestRequest = useAppSelector(selectLatestOrderRequest);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const isPosmonDisconnected = useAppSelector(selectIsPosmonDisconnected);
  const validCartItems = useAppSelector(selectValidCartItems);
  const hypothesisCartItems = useAppSelector(selectHypothesisCartItems);

  // Retry re-sends the current cart; with nothing valid to send, sendOrder
  // returns early and the click would be dead — hide the banner instead.
  // (Loyalty-only carts are also hidden; acceptable, the stuck lines the
  // banner points at are gone in that case too.)
  const hasCartItems =
    Object.values(validCartItems).length > 0 ||
    Object.values(hypothesisCartItems).length > 0;

  // A terminal POS rejection (e.g. 7008) means no retry can ever succeed for
  // this session's order — don't offer one.
  const isTerminalPosError = TERMINAL_ORDER_STATUS_CODES.includes(
    latestRequest?.orderStatusResponse?.data?.status?.code
  );

  const [isCheckOverdue, setIsCheckOverdue] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  // Seconds left on the auto-resend countdown; null = no auto-resend armed
  // (flag off, this episode's auto-send already spent, or banner not showing).
  const [autoRetrySecondsRemaining, setAutoRetrySecondsRemaining] = useState<
    number | null
  >(null);
  const [isAutoRetrySpent, setIsAutoRetrySpent] = useState(false);

  // The retry's own send (or any cart-change send) landed as a new latest
  // request — the round trip is over, allow retrying again.
  const latestRequestId = latestRequest?.id;
  useEffect(() => {
    setIsRetrying(false);
  }, [latestRequestId]);

  // Fallback: if the resend never records a new request (e.g. the menu-module
  // HTTP call failed first), re-enable after the check timeout instead of
  // leaving the button dead.
  useEffect(() => {
    if (!isRetrying) {
      return undefined;
    }
    const timerId = setTimeout(
      () => setIsRetrying(false),
      timeoutSeconds * 1000
    );
    return () => clearTimeout(timerId);
  }, [isRetrying, timeoutSeconds]);

  const isEligible = Boolean(
    isEnabled &&
      timeoutSeconds > 0 &&
      // Finals keep the overdue clock even on an empty cart — the finish
      // chain's clearCart() empties it, and the banner still owes the agent
      // an honest "unconfirmed" (never a forever-green "awaiting POS").
      (hasCartItems || latestRequest?.isFinal) &&
      !isPosmonDisconnected &&
      !isTerminalPosError &&
      latestRequest &&
      !latestRequest.checkResponse &&
      latestRequest.orderSentAt
  );

  // Whether a retry may be OFFERED (the clock above may run without it).
  // Every resend path rebuilds the order from the LIVE cart, so retry needs
  // real items to send — and a stuck final is retryable only when the AI
  // submitted it (its reset waits for the check, so the cart is still live).
  // HITL-clicked finals get no retry, by decision: their cart is already
  // cleared, and a resend would send an empty final. Untagged requests
  // (origin undefined) count as agent-submitted — the safe default.
  const canRetry = Boolean(
    isEligible &&
      hasCartItems &&
      (!latestRequest?.isFinal || latestRequest?.origin === 'AI')
  );

  useEffect(() => {
    if (!isEligible) {
      setIsCheckOverdue(false);
      return undefined;
    }

    // Flip to overdue the moment the check deadline passes. If we mounted /
    // re-rendered already past it (e.g. an order_status arrived late), flip
    // immediately.
    const timeoutMs = timeoutSeconds * 1000;
    const remainingMs =
      new Date(latestRequest!.orderSentAt!).getTime() + timeoutMs - Date.now();
    if (remainingMs <= 0) {
      setIsCheckOverdue(true);
      return undefined;
    }
    setIsCheckOverdue(false);
    const timerId = setTimeout(() => setIsCheckOverdue(true), remainingMs);
    return () => clearTimeout(timerId);
  }, [isEligible, timeoutSeconds, latestRequest]);

  const doRetry = useCallback(
    (trigger: 'manual' | 'auto') => {
      if (isRetrying) {
        return; // A resend is already in flight for this request.
      }
      setIsRetrying(true);
      // Either kind of retry spends the episode's single auto-send — see the
      // hook docblock.
      setIsAutoRetrySpent(true);
      const requestId = latestRequest?.id ?? 'unknown';
      const waitedMs = latestRequest?.orderSentAt
        ? Date.now() - new Date(latestRequest.orderSentAt).getTime()
        : null;
      const message =
        trigger === 'auto'
          ? `Order auto-retry fired: no check event for request ${requestId}${
              waitedMs != null ? ` after ${waitedMs}ms` : ''
            }`
          : `Order retry button clicked: no check event for request ${requestId}${
              waitedMs != null ? ` after ${waitedMs}ms` : ''
            }`;
      logger.warn(message);
      // Stable code + structured metadata so retry usage and the wait-at-click
      // distribution are aggregable downstream (same shape as COUPON_ADDED).
      // Auto fires get their own code so manual clicks and auto-resends are
      // separable in NR.
      const payload: Partial<InfoTransmissionMessage> = {
        data: {
          message,
          type: 'METRIC',
          code: trigger === 'auto' ? 'ORDER_RETRY_AUTO' : 'ORDER_RETRY_CLICKED',
          metadata: { requestId, waitedMs, isFinal: !!latestRequest?.isFinal },
        },
      };
      dispatch(messagingActions.sendInfo(payload as any));
      dispatch(
        sendOrder({
          // A stuck FINAL must be resent as final — a non-final resend would
          // not recover the finish. If the POS actually has the original
          // final, the resend answers 7008 (terminal) and the banner shows
          // the rejected state — which in that context means the POS already
          // holds the order.
          ...(latestRequest?.isFinal ? { isFinal: true } : {}),
          ...(isUnifiedMenu
            ? {
                menuUnificationOrderFailureCallback,
                menuUnificationOrderSuccessCallback,
              }
            : {}),
        })
      );
    },
    [
      isRetrying,
      latestRequest,
      isUnifiedMenu,
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
      dispatch,
    ]
  );

  const { sendAgentInterception } = useAgentInterception();

  // Not doRetry itself: onClick would pass the click event into the trigger
  // param. Not a useCallback: sendAgentInterception is a fresh function every
  // render, and nothing effect-depends on retry — only the button's onClick.
  const retry = () => {
    if (!canRetry) {
      return; // No live cart to resend (or a HITL final) — nothing to send.
    }
    if (isAIAutoMode) {
      // sendOrder refuses to send while the AI drives, so a click here is a
      // takeover first (the ai slice ends auto mode synchronously on this
      // action, letting the resend below through) — the same interception any
      // other agent action performs.
      sendAgentInterception({
        data: {
          type: AgentInterceptionType.ORDER_CHANGED,
          description: 'Order retry clicked while AI was driving',
        },
      } as any);
    }
    doRetry('manual');
  };

  // The stuck episode is over — a check finally landed (or the request set was
  // torn down, e.g. session end). Re-arm the auto-retry for the next episode.
  const hasCheckResponse = Boolean(latestRequest?.checkResponse);
  useEffect(() => {
    if (!latestRequest || hasCheckResponse) {
      setIsAutoRetrySpent(false);
    }
  }, [latestRequest, hasCheckResponse]);

  // Auto-resend countdown. Armed only while the banner shows; anchored to
  // orderSentAt (banner-at + autoRetrySeconds) rather than an interval counter,
  // so re-renders can't stretch it. When the deadline is already past on
  // arrival (e.g. a remount mid-episode), fire immediately — same convention
  // as the overdue clock above.
  // The orderSentAt guard covers the render right after the latest request is
  // torn down, before the effect above has caught up and cleared
  // isCheckOverdue. !isAIAutoMode: the banner shows while the AI drives
  // (HITL-1505) but the AUTO-resend must not fire until the agent has taken
  // over — HITL never auto-sends orders under the AI.
  // !isFinal: a stuck final gets a MANUAL Retry only. An automatic duplicate
  // final is exactly the HITL-1513 incident class (the resend supersedes the
  // original, whose late check is then discarded) — resubmitting a final must
  // be a deliberate agent decision.
  const isAutoRetryArmed = Boolean(
    isCheckOverdue &&
      autoRetrySeconds > 0 &&
      !isAutoRetrySpent &&
      !isRetrying &&
      !isAIAutoMode &&
      !latestRequest?.isFinal &&
      latestRequest?.orderSentAt
  );
  useEffect(() => {
    if (!isAutoRetryArmed) {
      setAutoRetrySecondsRemaining(null);
      return undefined;
    }
    const deadlineMs =
      new Date(latestRequest!.orderSentAt!).getTime() +
      (timeoutSeconds + autoRetrySeconds) * 1000;
    const tick = () => {
      const remainingSeconds = Math.ceil((deadlineMs - Date.now()) / 1000);
      if (remainingSeconds <= 0) {
        doRetry('auto');
        return;
      }
      setAutoRetrySecondsRemaining(remainingSeconds);
    };
    tick();
    // 250ms so the displayed second flips close to the true boundary instead
    // of lagging up to a full second behind it.
    const intervalId = setInterval(tick, 250);
    return () => clearInterval(intervalId);
  }, [
    isAutoRetryArmed,
    timeoutSeconds,
    autoRetrySeconds,
    latestRequest,
    doRetry,
  ]);

  return { isCheckOverdue, canRetry, isRetrying, autoRetrySecondsRemaining, retry };
};

export default useOrderCheckRetry;
